function isPresentValue(val) {
  if (val == null) return false;
  const s = String(val).trim();
  if (!s) return false;
  const lower = s.toLowerCase();
  return lower !== 'null' && lower !== 'undefined';
}

export function extractBankFromCmr(extraction) {
  return {
    account_number: String(extraction?.bank_account || '').trim(),
    ifsc: String(extraction?.ifsc || '').trim().toUpperCase(),
    bank_name: String(extraction?.bank_name || '').trim(),
  };
}

export function hasBankAccountAndIfsc(fields) {
  const account = String(fields?.account_number || '').replace(/\D/g, '');
  const ifsc = String(fields?.ifsc || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return account.length > 0 && ifsc.length === 11;
}

export function extractBankFromStatusPrefill(bank) {
  return {
    account_number: String(bank?.account_number || '').trim(),
    ifsc: String(bank?.ifsc || bank?.ifsc_code || '').trim().toUpperCase(),
    bank_name: String(bank?.bank_name || '').trim(),
  };
}

export function hasUsableBankPrefill(fields) {
  return hasBankAccountAndIfsc(fields) || (isPresentValue(fields?.ifsc) && isPresentValue(fields?.account_number));
}

export function normalizeAccountDigitsForMatch(account) {
  return String(account || '').replace(/\D/g, '');
}

export function bankAccountsMatch(accountA, accountB) {
  const da = normalizeAccountDigitsForMatch(accountA);
  const db = normalizeAccountDigitsForMatch(accountB);
  if (!da || !db) return false;
  if (da === db) return true;
  if (da.length >= 4 && db.length >= 4) {
    return da.endsWith(db) || db.endsWith(da);
  }
  return false;
}

export function normalizeExistingBankRecord(bank, index = 0) {
  const raw = bank && typeof bank === 'object' ? bank : {};
  const parsed = extractBankFromStatusPrefill(raw);
  const accountDigits = normalizeAccountDigitsForMatch(parsed.account_number);
  const ifsc = String(parsed.ifsc || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
  const apiId = String(raw.bank_account_id ?? raw.id ?? '').trim();
  const optionKey = apiId || `saved-${index}-${accountDigits || 'na'}-${ifsc || 'na'}`;
  return {
    ...raw,
    optionKey,
    bank_account_id: apiId || optionKey,
    account_number: parsed.account_number || raw.account_number || '',
    ifsc: ifsc || parsed.ifsc,
    bank_name: parsed.bank_name || raw.bank_name || '',
    branch_name: String(raw.branch_name || '').trim(),
  };
}

export function normalizeExistingBanks(banks) {
  const list = Array.isArray(banks) ? banks : [];
  const seen = new Set();
  return list.map((bank, index) => {
    let normalized = normalizeExistingBankRecord(bank, index);
    if (seen.has(normalized.optionKey)) {
      normalized = { ...normalized, optionKey: `${normalized.optionKey}-${index}`, bank_account_id: `${normalized.bank_account_id}-${index}` };
    }
    seen.add(normalized.optionKey);
    return normalized;
  });
}

export function getBankOptionValue(bank) {
  const key = String(bank?.optionKey || bank?.bank_account_id || '').trim();
  return `saved:${key}`;
}

export function parseBankOptionKey(value) {
  if (!value || !String(value).startsWith('saved:')) return null;
  return String(value).slice('saved:'.length);
}

/**
 * Returns true when the system cannot automatically pick which bank to use:
 * 2+ saved banks, OR 1 saved + CMR with a different account.
 * Returns false when session bank is already usable (back-navigation).
 */
export function needsBankSourceResolution(existingBanks, cmrFields, sessionBank = null) {
  if (sessionBank && hasUsableBankPrefill(extractBankFromStatusPrefill(sessionBank))) {
    return false;
  }
  const banks = Array.isArray(existingBanks) ? existingBanks : [];
  const count = banks.length;
  const hasCmr = hasBankAccountAndIfsc(cmrFields);
  if (count >= 2) return true;
  if (count === 1 && hasCmr) {
    return !bankAccountsMatch(banks[0].account_number, cmrFields.account_number);
  }
  return false;
}
